import React, { useEffect, useMemo, useState } from "react";
import { SidebarProvider, useSidebar } from "units/dashboard/context/SidebarProvider";
import UserSidebar from "./components/UserSidebar";
import Header from "units/dashboard/components/Header";
import {
  getWobList,
  addAccessListPhone,
  changeListType,
  deleteAccessListPhone,
  getAccessListChatrooms,
} from "./services/wobListService";
import {
  Search, Plus, Trash2, ShieldBan, ShieldCheck, ChevronRight,
  ChevronLeft, X, Phone, Calendar, CheckCircle, AlertCircle,
  Users, ListChecks, ArrowLeftRight, Sparkles, MessageSquare,
} from "lucide-react";
import { useSelector } from "react-redux";
import { confirmToast } from "toast/toastHelper";
import { useNavigate } from "react-router-dom";
import Footer from "units/dashboard/components/Footer";
import WobListUnavailable from "./components/WobListUnavailable";
import { useTranslation } from "react-i18next";
import AnimatedBackground from "units/client/component/AnimatedBackground";

/* ================================================================
 *  Chip helper
 * ================================================================ */
const Chip = ({ icon: Icon, children, tone = "teal" }) => {
  const tones = {
    teal: "bg-teal-500/15 text-teal-700 dark:!text-teal-300 border-teal-500/30",
    amber: "bg-amber-400/20 text-amber-800 dark:!text-amber-200 border-amber-400/40",
    rose: "bg-rose-500/15 text-rose-700 dark:!text-rose-300 border-rose-500/30",
    emerald: "bg-emerald-500/15 text-emerald-700 dark:!text-emerald-300 border-emerald-500/30",
    slate: "bg-slate-500/10 text-slate-600 dark:!text-slate-300 border-slate-400/30",
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-bold border ${tones[tone]}`}>
      {Icon && <Icon size={10} />}
      {children}
    </span>
  );
};

/* ================================================================
 *  Main
 * ================================================================ */
const WobListInner = () => {
  const { t, i18n } = useTranslation(["user"]);

  const { isSidebarOpen, toggleSidebar } = useSidebar();
  const username = useSelector((state) => state.userInfo)?.username;
  const accessToken = useSelector((state) => state.auth.accessToken);
  const [listType, setListType] = useState("blacklist");
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [phone, setPhone] = useState("");
  const [isChangingListType, setIsChangingListType] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const itemsPerPage = 5;
  const [showChatroomsModal, setShowChatroomsModal] = useState(false);
  const [chatrooms, setChatrooms] = useState([]);
  const [isLoadingChatrooms, setIsLoadingChatrooms] = useState(false);
  const [chatroomsError, setChatroomsError] = useState("");
  const [listError, setListError] = useState(false);
  const [listErrorMessage, setListErrorMessage] = useState("");
  const navigate = useNavigate();
  const isEnglish = (i18n.language === "en");
  /* ---------- بستن سایدبار موبایل هنگام resize به دسکتاپ ---------- */
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024 && isSidebarOpen) toggleSidebar();
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isSidebarOpen, toggleSidebar]);

  const getList = async () => {
    try {
      setListError(false);
      const res = await getWobList(accessToken);
      setPhoneNumbers(res?.data?.entries || []);
    } catch (error) {
      console.error("Error fetching phone numbers:", error);
      setListErrorMessage(error?.response?.data?.error);
      setListError(true);
      setPhoneNumbers([]);
    }
  };

  useEffect(() => {
    getList();
  }, []);

  const filteredNumbers = useMemo(() => {
    const trimmed = search.trim();
    if (!trimmed) return phoneNumbers;
    return phoneNumbers.filter((item) => item.phone.includes(trimmed));
  }, [phoneNumbers, search]);

  const totalPages = Math.ceil(filteredNumbers.length / itemsPerPage);
  const paginatedNumbers = filteredNumbers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleListTypeChange = async () => {
    const newListType = listType === "blacklist" ? true : false;
    try {
      setIsChangingListType(true);
      const payload = { isPrivate: newListType };
      const res = await changeListType(payload, accessToken);
      if (res.ok) {
        setListType(newListType === true ? "whitelist" : "blacklist");
        setCurrentPage(1);
      }
    } catch (error) {
      console.error("Error changing list type:", error);
    } finally {
      setIsChangingListType(false);
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    setPhone("");
    setPhoneError("");
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setPhone(item.phone);
    setPhoneError("");
    setShowModal(true);
  };

  const validatePhone = (value) => {
    const phoneRegex = /^09[0-9]{9}$/;
    if (!value.trim()) return t("wobList.errors.phoneRequired");
    if (!phoneRegex.test(value.trim())) return t("wobList.errors.phoneInvalid");
    const exists = phoneNumbers.some(
      (item) => item.phone === value.trim() && (editingItem ? item.id !== editingItem.id : true)
    );
    if (exists) return t("wobList.errors.phoneExists");
    return "";
  };

  const handleSave = async () => {
    const error = validatePhone(phone);
    if (error) {
      setPhoneError(error);
      return;
    }
    try {
      if (editingItem) {
        setPhoneNumbers((prev) =>
          prev.map((item) =>
            item.id === editingItem.id ? { ...item, phone: phone.trim() } : item
          )
        );
      } else {
        const normalizedPhone = phone.trim().replace(/^0/, "");
        const payload = { phone: normalizedPhone, countryCode: "+98" };
        await addAccessListPhone(payload, accessToken);
        await getList();
      }
      setPhone("");
      setEditingItem(null);
      setPhoneError("");
      setShowModal(false);
    } catch (error) {
      setPhoneError(error?.response?.data?.error || t("wobList.errors.addError"));
    }
  };

  const handleDelete = (id) => {
    confirmToast(async () => {
      try {
        const res = await deleteAccessListPhone(id, accessToken);
        if (!res.ok) {
          setPhoneError(res.error);
          return;
        }
        await getList();
      } catch (error) {
        setPhoneError(t("wobList.errors.deleteError"));
      }
    });
  };

  const handleShowChatrooms = async (entryId) => {
    setShowChatroomsModal(true);
    setChatrooms([]);
    setChatroomsError("");
    setIsLoadingChatrooms(true);
    try {
      const res = await getAccessListChatrooms(entryId, accessToken);
      if (!res.ok) {
        setChatroomsError(res.error);
        return;
      }
      setChatrooms(res.data?.data || res.data || []);
    } catch (error) {
      setChatroomsError(t("wobList.errors.chatroomsError"));
    } finally {
      setIsLoadingChatrooms(false);
    }
  };

  const currentListCount = phoneNumbers.filter((item) => item.type === listType).length;

  /* ================================================================
   *  ERROR STATE
   * ================================================================ */
  if (listError) {
    return (
      <div className="h-screen flex flex-col overflow-hidden">
        <AnimatedBackground />
        <nav className="shrink-0 z-30 h-[70px] flex items-center border-b border-teal-500/20 dark:!border-teal-400/15 bg-white/85 dark:!bg-slate-900/85 backdrop-blur-xl">
          <Header />
        </nav>
        <div className="flex-1 min-h-0 relative">

          {/* 👈 جدید — backdrop موبایل، دقیقاً مثل نسخه‌ی اصلی */}
          {isSidebarOpen && (
            <div
              onClick={toggleSidebar}
              className="lg:hidden fixed top-[70px] inset-x-0 bottom-0 z-30 bg-slate-900/50 dark:!bg-black/60 backdrop-blur-sm transition-opacity duration-300"
              aria-hidden="true"
            />
          )}

          <aside
            className={`
            fixed top-[70px] bottom-0 rtl:right-0 ltr:left-0 w-64 z-40
            transform transition-transform duration-300 ease-out
            ${isSidebarOpen ? "translate-x-0" : "rtl:translate-x-full ltr:-translate-x-full"}
            lg:!translate-x-0
            rtl:border-l ltr:border-r border-teal-500/10 dark:!border-teal-400/10
            bg-gradient-to-b from-white/95 via-teal-50/60 to-white/95
            dark:!from-slate-900/95 dark:!via-slate-900/98 dark:!to-slate-900/95
            backdrop-blur-xl shadow-[0_0_40px_-10px_rgba(15,118,110,0.4)] lg:shadow-none
            print:hidden overflow-hidden
          `}
          >
            <UserSidebar />
          </aside>

          <main className="h-full flex flex-col lg:rtl:pr-64 lg:ltr:pl-64 min-w-0">
            <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden custom-scrollbar">
              <div className="px-3 sm:px-4 md:px-6 py-6">
                <WobListUnavailable onRetry={getList} listErrorMessage={listErrorMessage} />
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  /* ================================================================
   *  RENDER
   * ================================================================ */
  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gradient-to-br from-slate-50 via-white to-teal-50/40 dark:!from-slate-950 dark:!via-slate-900 dark:!to-slate-950">

      {/* ================= Header ================= */}
      <nav className="shrink-0 z-30 h-[70px] flex items-center border-b border-teal-500/20 dark:!border-teal-400/15 bg-white/85 dark:!bg-slate-900/85 backdrop-blur-xl shadow-[0_4px_30px_-12px_rgba(15,118,110,0.35)] print:hidden">
        <Header />
      </nav>

      {/* ================= Body ================= */}
      <div className="flex-1 min-h-0 relative">

        {isSidebarOpen && (
          <div
            onClick={toggleSidebar}
            className="lg:hidden fixed top-[70px] inset-x-0 bottom-0 z-30 bg-slate-900/50 dark:!bg-black/60 backdrop-blur-sm transition-opacity duration-300"
            aria-hidden="true"
          />
        )}

        <aside
          className={`
            fixed top-[70px] bottom-0 rtl:right-0 ltr:left-0 w-64 z-40
            transform transition-transform duration-300 ease-out
            ${isSidebarOpen ? "translate-x-0" : "rtl:translate-x-full ltr:-translate-x-full"}
            lg:!translate-x-0
            rtl:border-l ltr:border-r border-teal-500/10 dark:!border-teal-400/10
            bg-gradient-to-b from-white/95 via-teal-50/60 to-white/95
            dark:!from-slate-900/95 dark:!via-slate-900/98 dark:!to-slate-900/95
            backdrop-blur-xl shadow-[0_0_40px_-10px_rgba(15,118,110,0.4)] lg:shadow-none
            print:hidden overflow-hidden
          `}
        >
          <UserSidebar />
        </aside>

        <main className="h-full flex flex-col lg:rtl:pr-64 lg:ltr:pl-64 min-w-0">
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden custom-scrollbar">
            <div className="px-3 sm:px-4 md:px-6 py-5 sm:py-6">

              {/* ============ Page Header ============ */}
              <div className="max-w-6xl mx-auto mb-5">
                <div className="flex items-center gap-2.5">
                  <span className="shrink-0 flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-amber-300/30 to-teal-400/20 border border-amber-300/40">
                    <Users size={20} className="text-amber-500" />
                  </span>
                  <div className="min-w-0">
                    <h1 className="text-lg sm:text-2xl font-bold bg-gradient-to-l from-teal-700 via-teal-600 to-amber-500 dark:!from-amber-200 dark:!via-teal-300 dark:!to-teal-400 bg-clip-text text-transparent">
                      {t("wobList.pageTitle")}
                    </h1>
                    <p className="text-[11px] sm:text-xs text-slate-500 dark:!text-slate-400 mt-1">
                      {t("wobList.pageSubtitle")}
                    </p>
                  </div>
                </div>
              </div>

              {/* ============ Top Info Row (compact) ============ */}
              <div className="max-w-6xl mx-auto mb-5">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">

                  {/* ---- List Type Card ---- */}
                  <div className="relative overflow-hidden rounded-2xl bg-white/70 dark:!bg-slate-900/70 backdrop-blur-xl border border-teal-500/15 dark:!border-teal-400/15 shadow-[0_10px_40px_-20px_rgba(15,118,110,0.4)] dark:!shadow-[0_10px_40px_-20px_rgba(0,0,0,0.85)]">
                    <div className={`absolute top-0 inset-x-0 h-[2px] bg-gradient-to-l from-transparent ${listType === "blacklist" ? "via-amber-400/80" : "via-teal-400/80"} to-transparent`} />
                    <div className="pointer-events-none absolute -top-16 -right-16 h-32 w-32 rounded-full bg-amber-300/10 blur-3xl dark:!bg-amber-400/5" />

                    <div className="relative p-4 flex items-center gap-3">
                      <span
                        className={`
                          shrink-0 flex h-12 w-12 items-center justify-center rounded-xl
                          border
                          ${listType === "blacklist"
                            ? "bg-gradient-to-br from-amber-300/40 to-amber-500/20 border-amber-300/50 text-amber-600 dark:!text-amber-300"
                            : "bg-gradient-to-br from-teal-300/40 to-teal-500/20 border-teal-400/50 text-teal-600 dark:!text-teal-300"
                          }
                        `}
                      >
                        {listType === "blacklist" ? <ShieldBan size={22} /> : <ShieldCheck size={22} />}
                      </span>

                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] font-bold text-slate-500 dark:!text-slate-400 tracking-wide">
                          {t("wobList.currentStatus")}
                        </p>
                        <p className="text-sm font-extrabold text-slate-800 dark:!text-amber-100 mt-0.5">
                          {listType === "blacklist" ? t("wobList.blacklist") : t("wobList.whitelist")}{" "}
                          <span className={`text-amber-600 dark:!text-amber-300 tabular-nums ${isEnglish && 'font-sans'}`}>
                            ({currentListCount} {t("wobList.numberUnit")})
                          </span>
                        </p>
                      </div>

                      <button
                        onClick={handleListTypeChange}
                        disabled={isChangingListType}
                        className={`
                          group shrink-0
                          flex items-center justify-center gap-1.5
                          px-3 sm:px-4 py-2.5 rounded-xl
                          text-[11px] sm:text-xs font-bold text-white
                          transition-all duration-300
                          hover:scale-[1.03] active:scale-95
                          disabled:opacity-60 disabled:cursor-not-allowed
                          ${listType === "blacklist"
                            ? "bg-gradient-to-l from-amber-500 to-amber-400 shadow-[0_6px_20px_-6px_rgba(251,191,36,0.8)]"
                            : "bg-gradient-to-l from-teal-700 to-teal-500 shadow-[0_6px_20px_-6px_rgba(15,118,110,0.8)]"
                          }
                        `}
                      >
                        {isChangingListType ? (
                          <>
                            <span className="inline-block h-3.5 w-3.5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                            <span className="hidden sm:inline">{t("wobList.changingListType")}</span>
                          </>
                        ) : (
                          <>
                            <ArrowLeftRight size={14} />
                            <span className="hidden sm:inline">
                              {listType === "blacklist" ? t("wobList.makePrivate") : t("wobList.makePublic")}
                            </span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* ---- Add Number Card ---- */}
                  <div className="relative overflow-hidden rounded-2xl bg-white/70 dark:!bg-slate-900/70 backdrop-blur-xl border border-teal-500/15 dark:!border-teal-400/15 shadow-[0_10px_40px_-20px_rgba(15,118,110,0.4)] dark:!shadow-[0_10px_40px_-20px_rgba(0,0,0,0.85)]">
                    <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-l from-transparent via-amber-400/70 to-transparent" />

                    <div className="relative p-4 flex items-center gap-3">
                      <span className="shrink-0 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-teal-300/40 to-teal-500/20 border border-teal-400/50 text-teal-600 dark:!text-teal-300">
                        <ListChecks size={22} />
                      </span>

                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-extrabold text-slate-800 dark:!text-amber-100">
                          {t("wobList.addNumberCard")}
                        </p>
                        <p className="text-[10px] text-slate-500 dark:!text-slate-400 mt-0.5 line-clamp-1 ">
                          {listType === "blacklist"
                            ? t("wobList.blacklistHint")
                            : t("wobList.whitelistHint")}
                        </p>
                      </div>

                      <button
                        onClick={handleAdd}
                        className="
                          group shrink-0
                          flex items-center justify-center gap-1.5
                          px-3 sm:px-4 py-2.5 rounded-xl
                          text-[11px] sm:text-xs font-bold text-white
                          bg-gradient-to-l from-teal-700 via-teal-600 to-teal-500
                          shadow-[0_6px_20px_-6px_rgba(15,118,110,0.8)]
                          hover:scale-[1.03] active:scale-95
                          transition-all duration-300
                        "
                      >
                        <Plus size={14} />
                        <span className="hidden sm:inline">{t("wobList.addShort")}</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* ============ List Section ============ */}
              <div className="max-w-6xl mx-auto relative rounded-2xl bg-white/70 dark:!bg-slate-900/70 backdrop-blur-xl border border-teal-500/15 dark:!border-teal-400/15 shadow-[0_10px_40px_-20px_rgba(15,118,110,0.4)] dark:!shadow-[0_10px_40px_-20px_rgba(0,0,0,0.85)] overflow-hidden">

                {/* خط طلایی بالا */}
                <div className="pointer-events-none absolute top-0 inset-x-0 h-[2px] bg-gradient-to-l from-transparent via-amber-400/70 to-transparent" />

                {/* Header */}
                <div className="relative p-4 border-b border-teal-500/10 dark:!border-teal-400/10">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">

                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className="shrink-0 flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-amber-300/40 to-teal-400/20 border border-amber-300/40">
                        <Phone size={14} className="text-amber-500" />
                      </span>
                      <div className="min-w-0">
                        <h2 className="text-sm font-bold text-slate-800 dark:!text-amber-100">
                          {t("wobList.listTitle")}
                        </h2>
                        <p className="text-[10px] text-slate-500 dark:!text-slate-400 mt-0.5">
                          <span className={`font-bold text-amber-600 dark:!text-amber-300 tabular-nums ${isEnglish && 'font-sans'}`}>
                            {filteredNumbers.filter((item) => item.type === listType).length}
                          </span>{" "}
                          {t("wobList.foundCount")}
                        </p>
                      </div>
                    </div>

                    <div className="relative w-full sm:w-72">
                      <Search size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                      <input
                        type="text"
                        value={search}
                        onChange={(e) => {
                          setSearch(e.target.value);
                          setCurrentPage(1);
                        }}
                        placeholder={t("wobList.searchPlaceholder")}
                        className="
                          w-full pr-9 pl-3 py-2.5 rounded-xl text-xs
                          bg-white/60 dark:!bg-slate-800/60
                          text-slate-800 dark:!text-slate-100
                          border border-teal-500/20 dark:!border-teal-400/20
                          focus:outline-none focus:ring-2 focus:ring-amber-300/60 focus:border-amber-300/60
                          transition-all duration-200
                          placeholder:text-slate-400 dark:!placeholder:text-slate-500
                        "
                      />
                    </div>
                  </div>
                </div>

                {/* Table */}
                <div className="relative overflow-x-auto">
                  <table className="w-full text-xs sm:text-sm text-right min-w-[640px]">
                    <thead>
                      <tr className="border-b border-teal-500/15 dark:!border-teal-400/15 bg-gradient-to-l from-teal-500/5 via-transparent to-amber-300/5">
                        <th className="px-4 py-3 text-[11px] font-bold text-slate-500 dark:!text-slate-400 text-center whitespace-nowrap">
                          <span className="flex items-center justify-center gap-1.5">
                            <span className="h-1 w-1 rounded-full bg-amber-400" />
                            {t("wobList.table.row")}
                          </span>
                        </th>
                        <th className="px-4 py-3 text-[11px] font-bold text-slate-500 dark:!text-slate-400 text-center whitespace-nowrap">
                          {t("wobList.table.phone")}
                        </th>
                        <th className="px-4 py-3 text-[11px] font-bold text-slate-500 dark:!text-slate-400 text-center whitespace-nowrap">
                          {t("wobList.table.chatrooms")}
                        </th>
                        <th className="px-4 py-3 text-[11px] font-bold text-slate-500 dark:!text-slate-400 text-center whitespace-nowrap">
                          <span className="inline-flex items-center gap-1">
                            <Calendar size={11} />
                            {t("wobList.table.createdAt")}
                          </span>
                        </th>
                        <th className="px-4 py-3 text-[11px] font-bold text-slate-500 dark:!text-slate-400 text-center whitespace-nowrap">
                          {t("wobList.table.actions")}
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {paginatedNumbers.length > 0 ? (
                        paginatedNumbers.map((item, index) =>
                          item.type === listType && (
                            <tr
                              key={item.id}
                              className="group border-b border-teal-500/8 dark:!border-teal-400/8 hover:bg-teal-500/5 dark:!hover:bg-teal-400/5 transition-colors"
                            >
                              <td className="px-4 py-3 text-center text-[11px] tabular-nums text-slate-500 dark:!text-slate-400">
                                {(currentPage - 1) * itemsPerPage + index + 1}
                              </td>

                              <td className="px-4 py-3 text-center">
                                <div className="inline-flex items-center gap-2">
                                  <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-br from-amber-300 to-teal-400 shadow-[0_0_6px_rgba(251,191,36,0.6)]" />
                                  <span className="font-extrabold text-slate-800 dark:!text-amber-100 tabular-nums font-mono">
                                    0{item.phone}
                                  </span>
                                </div>
                              </td>

                              <td className="px-4 py-3 text-center">
                                <button
                                  onClick={() => handleShowChatrooms(item.id)}
                                  className="
                                    inline-flex items-center gap-1.5
                                    px-2.5 py-1 rounded-lg
                                    text-[11px] font-extrabold tabular-nums
                                    text-teal-700 dark:!text-teal-300
                                    bg-teal-500/15 hover:bg-teal-500/25
                                    border border-teal-500/30 hover:border-teal-400/60
                                    transition-all duration-200
                                    hover:scale-105 active:scale-95
                                  "
                                >
                                  <MessageSquare size={10} />
                                  {item.chatroomCount}
                                </button>
                              </td>

                              <td className="px-4 py-3 text-center text-[11px] text-slate-500 dark:!text-slate-400 tabular-nums">
                                {item.createdAt}
                              </td>

                              <td className="px-4 py-3 text-center">
                                <button
                                  onClick={() => handleDelete(item.id)}
                                  className="
                                    h-8 w-8 inline-flex items-center justify-center
                                    rounded-lg
                                    text-rose-600 dark:!text-rose-300
                                    bg-rose-500/10 hover:bg-rose-500/20
                                    border border-rose-400/40 hover:border-rose-400/70
                                    transition-all duration-200
                                    hover:scale-110 active:scale-95
                                  "
                                  title={t("wobList.table.delete")}
                                >
                                  <Trash2 size={13} />
                                </button>
                              </td>
                            </tr>
                          )
                        )
                      ) : (
                        <tr>
                          <td colSpan="5" className="text-center py-12">
                            <div className="flex flex-col items-center gap-2 text-slate-400 dark:!text-slate-500">
                              <div className="h-14 w-14 rounded-full border-2 border-dashed border-teal-400/40 flex items-center justify-center">
                                <Search size={20} className="animate-pulse text-teal-400/70" />
                              </div>
                              <p className="text-sm font-bold text-slate-500 dark:!text-slate-400">
                                {t("wobList.emptyState.title")}
                              </p>
                              <p className="text-[11px] text-slate-400 dark:!text-slate-500">
                                {t("wobList.emptyState.hint")}
                              </p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="relative flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-teal-500/10 dark:!border-teal-400/10">
                    <span className="text-[11px] text-slate-500 dark:!text-slate-400">
                      {t("wobList.pagination.page")} <span className="font-bold text-amber-600 dark:!text-amber-300 tabular-nums">{currentPage}</span> {t("wobList.pagination.of")} <span className="tabular-nums">{totalPages}</span>
                    </span>

                    <div className="flex items-center gap-1.5">
                      <button
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage((prev) => prev - 1)}
                        className="h-8 w-8 flex items-center justify-center rounded-lg text-teal-700 dark:!text-amber-200 bg-white/60 dark:!bg-white/5 border border-teal-500/20 dark:!border-teal-400/20 hover:border-amber-300/60 hover:bg-amber-300/10 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed hover:scale-105 active:scale-95 disabled:hover:scale-100"
                      >
                        <ChevronRight size={14} />
                      </button>

                      {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`
                            min-w-[2rem] h-8 px-2 rounded-lg text-xs font-bold tabular-nums transition-all duration-200
                            ${currentPage === page
                              ? "bg-gradient-to-l from-teal-700 to-teal-500 text-white shadow-[0_6px_20px_-6px_rgba(15,118,110,0.8)]"
                              : "bg-white/60 dark:!bg-white/5 text-teal-700 dark:!text-amber-200 border border-teal-500/20 dark:!border-teal-400/20 hover:border-amber-300/60 hover:bg-amber-300/10"
                            }
                          `}
                        >
                          {page}
                        </button>
                      ))}

                      <button
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage((prev) => prev + 1)}
                        className="h-8 w-8 flex items-center justify-center rounded-lg text-teal-700 dark:!text-amber-200 bg-white/60 dark:!bg-white/5 border border-teal-500/20 dark:!border-teal-400/20 hover:border-amber-300/60 hover:bg-amber-300/10 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed hover:scale-105 active:scale-95 disabled:hover:scale-100"
                      >
                        <ChevronLeft size={14} />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="max-w-6xl mx-auto mt-8">
                <Footer />
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* ================= Add/Edit Modal ================= */}
      {showModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50 dark:!bg-black/70 backdrop-blur-sm px-4">
          <div className="w-full max-w-md rounded-2xl overflow-hidden bg-white dark:!bg-slate-900 border border-teal-500/25 dark:!border-teal-400/20 shadow-[0_25px_60px_-20px_rgba(15,118,110,0.6)] dark:!shadow-[0_25px_60px_-20px_rgba(0,0,0,0.9)] animate-[fadeIn_0.2s_ease-out]">
            <div className="h-[2px] bg-gradient-to-l from-transparent via-amber-400/80 to-transparent" />

            <div className="p-5">
              {/* Header */}
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="flex items-center gap-2.5">
                  <span className="shrink-0 flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-amber-300/40 to-teal-400/20 border border-amber-300/40">
                    <Phone size={17} className="text-amber-500" />
                  </span>
                  <div>
                    <h3 className="text-sm font-bold text-slate-800 dark:!text-amber-100">
                      {editingItem ? t("wobList.addModal.editTitle") : t("wobList.addModal.addTitle")}
                    </h3>
                    <p className="text-[11px] text-slate-500 dark:!text-slate-400 mt-0.5">
                      {editingItem ? t("wobList.addModal.editSubtitle") : t("wobList.addModal.addSubtitle")}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => { setShowModal(false); setPhoneError(""); }}
                  className="h-8 w-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-rose-500 hover:bg-rose-500/10 transition-colors"
                >
                  <X size={16} />
                </button>
              </div>

              {/* Input */}
              <div>
                <label className="block text-[11px] font-bold text-slate-600 dark:!text-slate-300 mb-1.5">
                  {t("wobList.addModal.phoneLabel")}
                </label>
                <div className="relative">
                  <Phone size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => { setPhone(e.target.value); if (phoneError) setPhoneError(""); }}
                    placeholder={t("wobList.addModal.phonePlaceholder")}
                    dir="ltr"
                    className={`
                      w-full pr-9 pl-3 py-2.5 rounded-xl text-sm tabular-nums text-left
                      bg-white/70 dark:!bg-slate-800/60
                      text-slate-800 dark:!text-slate-100
                      border transition-all duration-200
                      focus:outline-none focus:ring-2
                      ${phoneError
                        ? "border-rose-400/60 focus:ring-rose-400/40 focus:border-rose-400"
                        : "border-teal-500/20 dark:!border-teal-400/20 focus:ring-amber-300/60 focus:border-amber-300/60"
                      }
                    `}
                  />
                </div>

                {phoneError ? (
                  <div className="mt-2 flex items-center gap-1.5 rounded-lg bg-rose-500/10 border border-rose-400/30 px-2.5 py-1.5">
                    <AlertCircle size={12} className="text-rose-500 shrink-0" />
                    <p className="text-[11px] font-bold text-rose-600 dark:!text-rose-300">
                      {phoneError}
                    </p>
                  </div>
                ) : (
                  <div className="mt-2 flex items-center gap-1.5">
                    <span className="h-1 w-1 rounded-full bg-slate-400" />
                    <p className="text-[10px] text-slate-500 dark:!text-slate-400">
                      {t("wobList.addModal.phoneHint")}
                    </p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 mt-5">
                <button
                  onClick={handleSave}
                  className="
                    group relative overflow-hidden flex-1
                    flex items-center justify-center gap-2
                    py-2.5 px-4 rounded-xl
                    text-xs font-bold text-white
                    bg-gradient-to-l from-teal-700 via-teal-600 to-teal-500
                    shadow-[0_8px_24px_-8px_rgba(15,118,110,0.8)]
                    hover:from-teal-600 hover:via-teal-500 hover:to-teal-400
                    hover:scale-[1.02] active:scale-95
                    transition-all duration-300
                  "
                >
                  <span className="pointer-events-none absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                  {editingItem ? <CheckCircle size={14} className="relative" /> : <Plus size={14} className="relative" />}
                  <span className="relative">
                    {editingItem ? t("wobList.addModal.saveChanges") : t("wobList.addModal.addNumber")}
                  </span>
                </button>

                <button
                  onClick={() => { setShowModal(false); setPhoneError(""); }}
                  className="flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-bold text-slate-700 dark:!text-slate-200 bg-white/60 dark:!bg-white/5 border border-teal-500/20 dark:!border-teal-400/20 hover:border-amber-300/60 hover:bg-amber-300/10 transition-all duration-200"
                >
                  {t("wobList.addModal.cancel")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================= Chatrooms Modal ================= */}
      {showChatroomsModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50 dark:!bg-black/70 backdrop-blur-sm px-4">
          <div className="w-full max-w-2xl rounded-2xl overflow-hidden bg-white dark:!bg-slate-900 border border-teal-500/25 dark:!border-teal-400/20 shadow-[0_25px_60px_-20px_rgba(15,118,110,0.6)] dark:!shadow-[0_25px_60px_-20px_rgba(0,0,0,0.9)] animate-[fadeIn_0.2s_ease-out]">
            <div className="h-[2px] bg-gradient-to-l from-transparent via-amber-400/80 to-transparent" />

            {/* Header */}
            <div className="flex items-center justify-between gap-3 p-4 border-b border-teal-500/10 dark:!border-teal-400/10">
              <div className="flex items-center gap-2.5">
                <span className="shrink-0 flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-amber-300/40 to-teal-400/20 border border-amber-300/40">
                  <MessageSquare size={17} className="text-amber-500" />
                </span>
                <div>
                  <h3 className="text-sm font-bold text-slate-800 dark:!text-amber-100">
                    {t("wobList.chatroomsModal.title")}
                  </h3>
                  <p className="text-[11px] text-slate-500 dark:!text-slate-400 mt-0.5">
                    {t("wobList.chatroomsModal.subtitle")}
                  </p>
                </div>
              </div>
              <button
                onClick={() => { setShowChatroomsModal(false); setChatrooms([]); setChatroomsError(""); }}
                className="h-8 w-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-rose-500 hover:bg-rose-500/10 transition-colors"
              >
                <X size={16} />
              </button>
            </div>

            {/* Body */}
            <div className="p-4 max-h-[60vh] overflow-y-auto custom-scrollbar">

              {isLoadingChatrooms && (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <span className="h-8 w-8 rounded-full border-2 border-teal-500 border-t-transparent animate-spin" />
                  <p className="text-xs text-slate-500 dark:!text-slate-400">
                    {t("wobList.chatroomsModal.loading")}
                  </p>
                </div>
              )}

              {!isLoadingChatrooms && chatroomsError && (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <span className="h-12 w-12 flex items-center justify-center rounded-full bg-rose-500/15 border border-rose-400/40">
                    <AlertCircle size={22} className="text-rose-500" />
                  </span>
                  <p className="text-xs text-rose-600 dark:!text-rose-300 text-center font-bold">
                    {chatroomsError}
                  </p>
                </div>
              )}

              {!isLoadingChatrooms && !chatroomsError && chatrooms.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <div className="h-14 w-14 rounded-full border-2 border-dashed border-teal-400/40 flex items-center justify-center">
                    <MessageSquare size={22} className="animate-pulse text-teal-400/70" />
                  </div>
                  <p className="text-sm font-bold text-slate-500 dark:!text-slate-400">
                    {t("wobList.chatroomsModal.empty")}
                  </p>
                  <p className="text-[11px] text-slate-400 dark:!text-slate-500">
                    {t("wobList.chatroomsModal.emptyHint")}
                  </p>
                </div>
              )}

              {!isLoadingChatrooms && !chatroomsError && chatrooms.length > 0 && (
                <div className="space-y-2">
                  {chatrooms.map((room, index) => (
                    <div
                      key={room.id}
                      onClick={() =>
                        navigate(`/bot/chat/${username}`, {
                          state: { navigatedFromWobList: true, chatroomIdd: room.id },
                        })
                      }
                      className="
                        group flex items-center gap-3 p-3 rounded-xl
                        bg-white/60 dark:!bg-white/5
                        border border-teal-500/15 dark:!border-teal-400/15
                        hover:border-amber-300/60 hover:bg-amber-300/5
                        cursor-pointer
                        transition-all duration-200
                        hover:scale-[1.01]
                      "
                    >
                      <span className="shrink-0 h-10 w-10 flex items-center justify-center rounded-lg bg-gradient-to-br from-amber-300/40 to-teal-400/20 border border-amber-300/40 text-amber-600 dark:!text-amber-300">
                        <MessageSquare size={16} />
                      </span>

                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-bold text-slate-800 dark:!text-slate-100 truncate">
                          {room.name || t("wobList.chatroomsModal.unnamed")}
                        </p>
                        <p className="text-[10px] text-slate-500 dark:!text-slate-400 mt-0.5 tabular-nums">
                          {room.createdAt || t("wobList.chatroomsModal.unknownDate")}
                        </p>
                      </div>

                      <Chip tone="slate">#{index + 1}</Chip>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between gap-3 p-4 border-t border-teal-500/10 dark:!border-teal-400/10">
              <span className="text-[11px] text-slate-500 dark:!text-slate-400">
                {t("wobList.chatroomsModal.totalChatrooms")}{" "}
                <span className="font-bold text-amber-600 dark:!text-amber-300 tabular-nums">
                  {chatrooms.length}
                </span>
              </span>

              <button
                onClick={() => { setShowChatroomsModal(false); setChatrooms([]); setChatroomsError(""); }}
                className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-slate-700 dark:!text-slate-200 bg-white/60 dark:!bg-white/5 border border-teal-500/20 dark:!border-teal-400/20 hover:border-amber-300/60 hover:bg-amber-300/10 transition-all duration-200"
              >
                {t("wobList.chatroomsModal.close")}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }

        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: rgba(20, 184, 166, 0.55) transparent;
          scroll-behavior: smooth;
        }
        .custom-scrollbar::-webkit-scrollbar { width: 10px; height: 10px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; border-radius: 999px; margin: 6px 0; }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          border-radius: 999px; border: 2px solid transparent; background-clip: padding-box;
          background-image: linear-gradient(180deg, rgba(20,184,166,0.55), rgba(45,212,191,0.7), rgba(251,191,36,0.55));
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-image: linear-gradient(180deg, rgba(20,184,166,0.9), rgba(45,212,191,1), rgba(251,191,36,0.9));
          box-shadow: 0 0 12px -2px rgba(20,184,166,0.7);
        }
        .dark .custom-scrollbar { scrollbar-color: rgba(251,191,36,0.45) transparent; }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb {
          background-image: linear-gradient(180deg, rgba(20,184,166,0.55), rgba(94,234,212,0.65), rgba(251,191,36,0.65));
        }
      `}</style>
    </div>
  );
};

const WobList = () => (
  <SidebarProvider>
    <WobListInner />
  </SidebarProvider>
);

export default WobList;